import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import {
  AuthenticationService,
  MarketplacePermission,
  IAccount,
} from '@libs/authentication/services';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { isPlatformServer } from '@angular/common';
import { BETA_CUSTOMERS } from './config/beta-customers';

declare let window: any;
declare let document: any;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class IntercomService {
  appId: string;
  intercomCustomers = BETA_CUSTOMERS;

  constructor(
    private auth: AuthenticationService,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {
    if (isPlatformServer(platformId)) {
      return;
    }

    this.appId =
      window.location && window.location.hostname.match('snagajob.com')
        ? 'dtz2ttzi'
        : 'zc3raarz';
    this.auth.permission$
      .pipe(
        untilDestroyed(this),
        filter((p: MarketplacePermission) => !!p?.emailAddress),
        distinctUntilChanged(),
      )
      .subscribe((profile: MarketplacePermission) => {
        const params = new URLSearchParams(window.location.search);
        this.initIntercom(profile);

        if (
          this.intercomCustomers.includes(profile.accountId) ||
          params.get('intercom') === 'true'
        ) {
          this.show();
        } else {
          this.hide();
        }
      });
  }

  isIntercomAvailable(): boolean {
    return window.Intercom && window.intercomSettings;
  }

  track(event: string, metadata: any = null) {
    if (!this.isIntercomAvailable()) {
      return;
    }

    window.Intercom('trackEvent', event, metadata);
  }

  hide(): void {
    window.Intercom('update', {
      hide_default_launcher: true,
    });
  }

  show(): void {
    window.Intercom('update', {
      hide_default_launcher: false,
    });
  }

  initIntercom(profile: MarketplacePermission) {
    const account: IAccount = this.auth.account$.getValue();

    window.intercomSettings = {
      app_id: this.appId,
      user_id: profile.id,
      email: profile.emailAddress,
      business_name: account.name,
      netsuite_id: account.netSuiteId,
      purchase_type: account.accountType,
      salesforce_id: account.salesForceId,
      admin:
        profile.roles?.includes('Admin') ||
        profile.roles?.includes('Admin (Snagajob)'),
    };
    const w = window;
    const ic = w.Intercom;

    if (typeof ic === 'function') {
      ic('reattach_activator');
      ic('update', w.intercomSettings);
    } else {
      const d = document;
      const i: any = function () {
        i.c(arguments);
      };

      i.q = [];
      i.c = function (args) {
        i.q.push(args);
      };

      w.Intercom = i;
      const s = d.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = 'https://widget.intercom.io/widget/' + this.appId;

      const x = d.getElementsByTagName('script')[0];
      x.parentNode.insertBefore(s, x);
    }
  }
}
